import { createFileRoute, redirect } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import {
  useNotifications,
  useUnreadCount,
  useMarkRead,
  useMarkAllRead,
  type Notification,
} from "../../../hooks/useNotifications";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { useState, useEffect, useRef } from "react";
import { toast } from "../../../components/ui/sonner";

export const Route = createFileRoute("/_admin/notifications/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  pendingComponent: () => <PageSkeleton rows={8} variant="list" />,
  errorComponent: createRouteError("Notifications"),
  component: NotificationsPage,
});

function NotificationsPage() {
  const { user, loading, isAuthenticated } = useAuth();

  // During SSR or while auth is loading, render a minimal shell
  if (loading || !isAuthenticated || !user) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  return <NotificationsContent userId={user.id} />;
}

// ---------------------------------------------------------------------------
// NotificationsContent — rendered only when user is authenticated
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

function NotificationsContent({ userId }: { userId: string }) {
  // Access token from AuthProvider context (server-side JWT validation)
  const { session } = useAuth();
  const accessToken = session?.access_token ?? "";

  // Pagination state
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<Notification[]>([]);

  const {
    data: listData,
    isLoading: listLoading,
    error: listError,
  } = useNotifications({ userId, accessToken, page, pageSize: PAGE_SIZE });

  const { data: unreadCount = 0 } = useUnreadCount(userId, accessToken);

  const markRead = useMarkRead(userId);
  const markAllRead = useMarkAllRead(userId);

  // Accumulate pages of results.
  // Use a ref to track which pages have already been loaded so that a
  // slow/out-of-order response for a previous page doesn't duplicate data.
  const loadedPagesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!listData?.notifications) return;
    if (page === 1) {
      // Fresh load or reset after mutation — replace the accumulated list
      loadedPagesRef.current = new Set([1]);
      setAccumulated(listData.notifications as Notification[]);
    } else if (!loadedPagesRef.current.has(page)) {
      // Only append a page's results once (prevents duplicate appends from
      // late-arriving responses or React Strict Mode double-invocation)
      loadedPagesRef.current.add(page);
      setAccumulated((prev) => [
        ...prev,
        ...(listData.notifications as Notification[]),
      ]);
    }
  }, [listData, page]);

  // After mark-all-read, reset to page 1 so the list refreshes cleanly
  const handleMarkAllRead = () => {
    if (!accessToken) return;
    markAllRead.mutate(
      { accessToken },
      {
        onSuccess: () => {
          setPage(1);
          setAccumulated([]);
          toast.success("All notifications marked as read");
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : "Failed to mark notifications as read");
        },
      }
    );
  };

  const handleMarkRead = (notificationId: string) => {
    if (!accessToken) return;
    markRead.mutate({ notificationId, accessToken });
  };

  const handleLoadMore = () => setPage((p) => p + 1);

  const total = listData?.total ?? 0;
  const hasMore = accumulated.length < total;
  const isFetchingMore = listLoading && page > 1;

  // ------------------------------------------------------------------
  // Initial loading state (first page only)
  // ------------------------------------------------------------------
  if (listLoading && page === 1) {
    return (
      <PageShell unreadCount={unreadCount} onMarkAllRead={handleMarkAllRead}>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-20 rounded-lg border bg-muted animate-pulse"
            />
          ))}
        </div>
      </PageShell>
    );
  }

  // ------------------------------------------------------------------
  // Error state
  // ------------------------------------------------------------------
  if (listError) {
    return (
      <PageShell unreadCount={unreadCount} onMarkAllRead={handleMarkAllRead}>
        <div className="rounded-lg border bg-destructive/10 p-6 text-center">
          <p className="text-destructive font-medium mb-2">
            Failed to load notifications
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            {listError.message}
          </p>
          <Button
            variant="default"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Try again
          </Button>
        </div>
      </PageShell>
    );
  }

  // ------------------------------------------------------------------
  // Empty state
  // ------------------------------------------------------------------
  if (!listLoading && !listError && accumulated.length === 0) {
    return (
      <PageShell unreadCount={unreadCount} onMarkAllRead={handleMarkAllRead}>
        <Card>
          <CardContent className="py-16 flex flex-col items-center gap-3">
            <span className="text-5xl">🔔</span>
            <p className="text-lg font-medium text-foreground">
              You&apos;re all caught up!
            </p>
            <p className="text-sm text-muted-foreground">
              No notifications to show right now.
            </p>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  // ------------------------------------------------------------------
  // Notification list
  // ------------------------------------------------------------------
  return (
    <PageShell unreadCount={unreadCount} onMarkAllRead={handleMarkAllRead}>
      <div className="space-y-2">
        {accumulated.map((notification) => (
          <NotificationRow
            key={notification.id}
            notification={notification}
            onMarkRead={handleMarkRead}
          />
        ))}
      </div>

      {/* Load more / pagination footer */}
      <div className="mt-4 text-center space-y-2">
        {hasMore && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadMore}
            disabled={isFetchingMore}
          >
            {isFetchingMore ? "Loading…" : "Load more"}
          </Button>
        )}

        <p className="text-xs text-muted-foreground">
          Showing {accumulated.length} of {total} notification
          {total !== 1 ? "s" : ""}
        </p>
      </div>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// PageShell — header + layout wrapper
// ---------------------------------------------------------------------------

function PageShell({
  children,
  unreadCount,
  onMarkAllRead,
}: {
  children: React.ReactNode;
  unreadCount: number;
  onMarkAllRead: () => void;
}) {
  return (
      <main className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {unreadCount > 0
                  ? `${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""}`
                  : "All notifications read"}
              </CardTitle>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onMarkAllRead}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Mark all read
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      </main>
  );
}

// ---------------------------------------------------------------------------
// NotificationRow — individual notification item
// ---------------------------------------------------------------------------

function NotificationRow({
  notification,
  onMarkRead,
}: {
  notification: Notification;
  onMarkRead: (id: string) => void;
}) {
  const timeAgo = formatTimeAgo(notification.created_at);

  return (
    <button
      onClick={() => {
        if (!notification.is_read) {
          onMarkRead(notification.id);
        }
      }}
      className={[
        "w-full text-left rounded-lg border p-4 transition-colors",
        notification.is_read
          ? "bg-card hover:bg-muted/50 opacity-70"
          : "bg-primary/5 border-primary/20 hover:bg-primary/10",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        {/* Unread indicator */}
        <div className="mt-1.5 flex-shrink-0">
          {notification.is_read ? (
            <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
          ) : (
            <div className="w-2 h-2 rounded-full bg-primary" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Type badge */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {notification.type.replace(/_/g, " ")}
            </span>
            {!notification.is_read && (
              <span className="text-xs text-primary font-medium">New</span>
            )}
          </div>

          {/* Message */}
          {notification.message_preview && (
            <p className="text-sm text-foreground line-clamp-2">
              {notification.message_preview}
            </p>
          )}

          {/* Timestamp */}
          <p className="text-xs text-muted-foreground mt-1">{timeAgo}</p>
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimeAgo(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diff = Math.max(0, now - then);

  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return new Date(isoString).toLocaleDateString();
}
